/* global google */
const util = require('./util')

const customMapStyle = '&style=feature:all|element:all|visibility:on&style=feature:all|element:labels|visibility:off|saturation:-100&style=feature:all|element:labels.text.fill|saturation:36|color:0x000000|lightness:40|visibility:off&style=feature:all|element:labels.text.stroke|visibility:off|color:0x000000|lightness:16&style=feature:all|element:labels.icon|visibility:off&style=feature:administrative|element:geometry.fill|color:0x000000|lightness:20&style=feature:administrative|element:geometry.stroke|color:0x000000|lightness:17|weight:1.2&style=feature:landscape|element:geometry|color:0x000000|lightness:20&style=feature:landscape|element:geometry.fill|color:0x4d6059&style=feature:landscape|element:geometry.stroke|color:0x4d6059&style=feature:landscape.natural|element:geometry.fill|color:0x4d6059&style=feature:poi|element:geometry|lightness:21&style=feature:poi|element:geometry.fill|color:0x4d6059&style=feature:poi|element:geometry.stroke|color:0x4d6059&style=feature:road|element:geometry|visibility:on|color:0x7f8d89&style=feature:road|element:geometry.fill|color:0x7f8d89&style=feature:road.highway|element:geometry.fill|color:0x7f8d89|lightness:17&style=feature:road.highway|element:geometry.stroke|color:0x7f8d89|lightness:29|weight:0.2&style=feature:road.arterial|element:geometry|color:0x000000|lightness:18&style=feature:road.arterial|element:geometry.fill|color:0x7f8d89&style=feature:road.arterial|element:geometry.stroke|color:0x7f8d89&style=feature:road.local|element:geometry|color:0x000000|lightness:16&style=feature:road.local|element:geometry.fill|color:0x7f8d89&style=feature:road.local|element:geometry.stroke|color:0x7f8d89&style=feature:transit|element:geometry|color:0x000000|lightness:19&style=feature:water|element:all|color:0x2b3638|visibility:on&style=feature:water|element:geometry|color:0x2b3638|lightness:17&style=feature:water|element:geometry.fill|color:0x24282b&style=feature:water|element:geometry.stroke|color:0x24282b&style=feature:water|element:labels|visibility:off&style=feature:water|element:labels.text|visibility:off&style=feature:water|element:labels.text.fill|visibility:off&style=feature:water|element:labels.text.stroke|visibility:off&style=feature:water|element:labels.icon|visibility:off'
const mapUrlStart = `https://maps.googleapis.com/maps/api/staticmap?&size=250x275${customMapStyle}`

const mapClass = 'location-map'
const removeButtonClass = 'location-map__remove'
const countContainerClass = 'location-map__count-container'
const countClass = 'location-map__count'

class LocationMapper {
  constructor (config) {
    this.mapContainer = document.querySelector(config.mapContainer)
    this.apiKey = config.googleMapsApiKey
    this.domain = config.domain
    this.locations = []
    this.showCount = Boolean(config.showCount)
    this.makeClickable = config.edit === undefined

    if (config.locations) {
      config.locations.reverse().forEach(loc => this.addLocation(loc))
    }
    if (config.edit) {
      this.editMode = true
      this.input = document.querySelector(config.edit.input)
      this.form = document.querySelector(config.edit.form)

      const googleMapsUrl = `https://maps.googleapis.com/maps/api/js?key=${config.googleMapsApiKey}&libraries=places&callback=onGoogleMapsReady`
      window.onGoogleMapsReady = this.initAutocomplete.bind(this)
      util.loadScript(googleMapsUrl, (err) => {
        if (err) {
          console.log('Error loading Google Maps')
        }
        this.setupForm(config.edit, !err)
      })
    }
  }

  setupForm (editConfig, autoCompleteEnabled) {
    this.form.addEventListener('submit', event => {
      if (!autoCompleteEnabled) {
        this.saveLocation(this.input.value)
      }
      event.preventDefault()
    })

    this.validation = document.querySelector(editConfig.validation)

    this.addRemoveButtons()
    this.listenForRemove()
  }

  // called only when google client is ready
  initAutocomplete () {
    const options = {
      types: ['locality', 'administrative_area_level_1', 'administrative_area_level_2', 'administrative_area_level_3', 'country']
    }
    // docs: https://developers.google.com/maps/documentation/javascript/places-autocomplete
    this.autocomplete = new google.maps.places.Autocomplete(this.input, options)
    this.autocomplete.addListener('place_changed', this.onAutocompleted.bind(this))
  }

  onAutocompleted () {
    const place = this.autocomplete.getPlace()
    if (!place.address_components) {
      // user probably hit "enter" without selecting a suggestion
      const suggestionText = getFirstAutocompleteSuggestion()
      return this.saveLocation(suggestionText || this.input.value)
    }
    this.saveLocation(this.input.value, place.address_components)
  }

  saveLocation (input, autocompleted) {
    util.postJson(`/${this.domain}/location`, { location: { input, autocompleted } })
      .then(json => {
        this.addLocation(json)
        this.input.placeholder = 'Add another location'
        this.input.focus()
        this.input.value = ''
      }).catch(err => {
        this.validation.innerText = 'Failed to add location. Location may be invalid. Please try another.'
        this.validation.removeAttribute('hidden')
        console.error('Unable to add location', err)
        window.Sentry && window.Sentry.captureException(err)
      })
  }

  addLocation (json) {
    const mapHtml = buildMap(this.apiKey, json, this.editMode, this.makeClickable)
    this.mapContainer.innerHTML = mapHtml + this.mapContainer.innerHTML
    this.locations.push(json)
    if (this.validation) {
      this.validation.setAttribute('hidden', '')
    }
    if (this.editMode) {
      // hook up new remove button
      this.listenForRemove()
    }

    if (this.showCount) {
      this.fetchCount(json).then(count => {
        displayLocationCount(json.id, count)
      }).catch(err => {
        console.error('Unable to fetch location count.', err)
        if (err.name === 'TypeError') return // ignore network errors
        window.Sentry && window.Sentry.captureException(err)
      })
    }
  }

  addRemoveButtons () {
    const maps = document.querySelectorAll(`.${mapClass}`)
    Array.from(maps).forEach(map => {
      map.innerHTML += buildRemoveButton()
    })
  }

  listenForRemove () {
    const removeButtons = document.querySelectorAll(`.${removeButtonClass}`)
    Array.from(removeButtons).forEach(button => {
      button.addEventListener('click', event => this.removeLocation(event), { once: true })
    })
  }

  removeLocation (event) {
    const mapElement = event.target.closest('.location-map')
    const selectedLocationId = parseInt(mapElement.getAttribute('data-id'), 10)

    const gridCell = mapElement.parentNode
    util.deleteRequest(`/${this.domain}/location/${selectedLocationId}`)
      .then(() => {
        gridCell.parentNode.removeChild(gridCell)
        this.locations = this.locations.filter(loc => loc.id !== selectedLocationId)
      }).catch(err => {
        this.validation.innerText = 'Failed to remove location. Please try again.'
        this.validation.removeAttribute('hidden')
        console.error('Unable to remove location.', err)
        window.Sentry && window.Sentry.captureException(err)
      })
  }

  fetchCount (location) {
    let url = '/profile/location/count?'
    if (location.country) {
      url += `&country=${location.country.code}`
    }
    if (location.region) {
      url += `&region=${location.region.code}`
    }
    if (location.city) {
      url += `&city=${location.city.code}`
    }
    return util.getJson(url).then(json => {
      return json.formattedCount
    }).catch(err => {
      window.Sentry && window.Sentry.captureException(err)
    })
  }
}

function displayLocationCount (locationId, count) {
  if (count <= 1) {
    // insufficient members to warrant displaying
    return
  }
  document.querySelector(`[data-id="${locationId}"] .${countContainerClass}`).removeAttribute('hidden')
  document.querySelector(`[data-id="${locationId}"] .${countClass}`).innerText = count
}

function buildRemoveButton () {
  return `<div class="location-map__remove-container"><button class="${removeButtonClass} button-over-dark">Remove</button></div>`
}

function buildMap (apiKey, location, editMode, makeClickable) {
  const { id, city, country, region } = location
  const [primaryLabel, secondaryLabel] = chooseMapLabels(city, region, country)
  const labels = `<small class="location-map__secondary">${secondaryLabel || ''}</small><div class="location-map__primary">${primaryLabel || ''}</div>`
  const mapLocation = encodeURIComponent([primaryLabel, secondaryLabel].filter(Boolean).join(',')).replace("'", '%27') // NOTE: encodeURIComponent will not escape ', so do it manually

  const mapBg = `style="background-image:url('${mapUrlStart}&key=${apiKey}&visible=${mapLocation}')"`
  const countPlaceholder = `<div hidden class="${countContainerClass}"><span class="${countClass}"></span><span class="location-map__count-label">.eco members<span></div>`
  const rootElement = makeClickable ? `a href="${buildCollectorLink(location)}" title="View members of the .eco community based in ${primaryLabel}"` : 'div'
  const rootElementEnd = makeClickable ? 'a' : 'div'
  const removeButton = editMode ? buildRemoveButton() : ''
  return `<div class="grid__cell"><${rootElement} class="${mapClass}" data-id="${id}" ${mapBg}><div class="location-map__labels">${labels}</div>${countPlaceholder}${removeButton}</${rootElementEnd}></div>`
}

function chooseMapLabels (city, region, country) {
  if (city) {
    let primaryLabel = city.label
    if (region && region.code.length <= 3 && region.label !== primaryLabel) {
      primaryLabel += `, ${region.code}`
    }
    return [primaryLabel, country && country.label]
  }
  if (region) {
    return [region.label, country && country.label]
  }
  return [country.label]
}

// TODO: pass through link from locationService.buildCollectorLink instead
function buildCollectorLink ({ country, region, city }) {
  const locs = [city, region, country].reduce((memo, loc) => {
    if (loc?.code) {
      memo.push(encodeURIComponent(loc?.code))
    } else if (memo.length) {
      memo.push('-')
    }
    return memo
  }, []).reverse()
  return `/profile/location/${locs.join('/')}`
}

function getFirstAutocompleteSuggestion () {
  // ugly hack to handle user just hitting 'enter', which google autocomplete API does not
  const firstSuggestion = document.querySelector('.pac-container .pac-item')
  if (!firstSuggestion) {
    return
  }
  const start = firstSuggestion.querySelector('.pac-item-query').innerText
  const end = firstSuggestion.querySelector(':nth-child(3)').innerText
  return `${start} ${end}`
}

module.exports = {
  init: function (config) {
    return new LocationMapper(config)
  }
}
